import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useUploadVersion } from "../hooks/useRepository";

interface Props {
  resourceId: string;
  resourceTitle: string;
  onClose: () => void;
}

export function UploadVersionModal({ resourceId, resourceTitle, onClose }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState("");
  const { mutateAsync: upload, isPending } = useUploadVersion();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    await upload({ id: resourceId, file, comment });
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Nowa wersja — {resourceTitle}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Plik <span className="text-red-500">*</span>
            </label>
            <FileDropZone
              files={file ? [file] : []}
              onFilesChange={(fs) => setFile(fs[0] ?? null)}
            />
          </div>

          {/* Change comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Opis zmian
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Co zmieniono w tej wersji?"
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={!file || isPending}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {isPending && <Loader2 size={13} className="animate-spin" />}
              Wgraj wersję
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
